import { useState, useCallback } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { 
  identifyCorruptedContent, 
  triggerContentRegeneration,
  getModulesMissingAudio,
  generateMissingAudio,
  ContentFixResult,
  FixProgress 
} from '@/services/contentFixService';
import { supabase } from '@/integrations/supabase/client';

interface CorruptedModule {
  moduleId: string;
  courseId: string;
  issue: string;
}

interface ContentQualityReport {
  overallScore: number;
  corruptedModules: CorruptedModule[];
  totalModules: number;
  healthyModules: number;
  corruptedCount: number;
  missingAudioCount: number;
  quizCoverage: number;
  contentCompleteness: number;
}

interface ContentQualityState {
  report: ContentQualityReport | null;
  isAnalyzing: boolean;
  isFixing: boolean;
  lastAnalyzed: Date | null;
  errors: string[];
  fixResult: ContentFixResult | null;
}

type ProgressCallback = (progress: FixProgress) => void;

export const useContentQuality = () => {
  const { userRole } = useSupabaseAuth();
  const [state, setState] = useState<ContentQualityState>({
    report: null,
    isAnalyzing: false,
    isFixing: false,
    lastAnalyzed: null,
    errors: [],
    fixResult: null
  });

  const canManageContent = userRole === 'admin' || userRole === 'staff';

  const analyzeContentQuality = useCallback(async () => {
    if (!canManageContent) return;

    setState(prev => ({ ...prev, isAnalyzing: true, errors: [] }));

    try {
      // Get real corrupted content from database
      const corruptedModules = await identifyCorruptedContent();
      
      // Get total module count
      const { count: totalModules } = await supabase
        .from('course_modules')
        .select('*', { count: 'exact', head: true })
        .eq('is_enabled', true);

      // Get modules missing audio
      const missingAudioModules = await getModulesMissingAudio();

      // Get quiz coverage
      const { count: modulesWithQuizzes } = await supabase
        .from('module_quizzes')
        .select('module_id', { count: 'exact', head: true });

      const total = totalModules || 0;
      const corruptedCount = corruptedModules.length;
      const healthyModules = total - corruptedCount;
      const overallScore = total > 0 ? Math.round((healthyModules / total) * 100) : 0;
      const quizCoverage = total > 0 ? Math.round(((modulesWithQuizzes || 0) / total) * 100) : 0;
      const contentCompleteness = total > 0 ? Math.round((healthyModules / total) * 100) : 0;

      const report: ContentQualityReport = {
        overallScore,
        corruptedModules,
        totalModules: total,
        healthyModules,
        corruptedCount,
        missingAudioCount: missingAudioModules.length,
        quizCoverage,
        contentCompleteness
      };
      
      setState(prev => ({
        ...prev,
        report,
        isAnalyzing: false,
        lastAnalyzed: new Date()
      }));

      return report;
    } catch (error) {
      console.error('Content quality analysis failed:', error);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        errors: [error instanceof Error ? error.message : 'Analysis failed']
      }));
    }
  }, [canManageContent]);

  const fixCorruptedContent = useCallback(async (
    moduleIds?: string[],
    onProgress?: ProgressCallback,
    enableAutoRetry: boolean = true
  ) => {
    if (!canManageContent) return;

    setState(prev => ({ ...prev, isFixing: true, errors: [] }));

    try {
      const idsToFix = moduleIds || state.report?.corruptedModules.map(m => m.moduleId) || [];
      
      if (idsToFix.length === 0) {
        setState(prev => ({ ...prev, isFixing: false }));
        return { totalCorrupted: 0, fixed: 0, failed: 0, moduleIds: [] };
      }

      const result = await triggerContentRegeneration(idsToFix, onProgress, enableAutoRetry);
      
      setState(prev => ({
        ...prev,
        isFixing: false,
        fixResult: result
      }));

      return result;
    } catch (error) {
      console.error('Content fix failed:', error);
      setState(prev => ({
        ...prev,
        isFixing: false,
        errors: [error instanceof Error ? error.message : 'Fix failed']
      }));
    }
  }, [canManageContent, state.report]);

  const fixMissingAudio = useCallback(async () => {
    if (!canManageContent) return;

    setState(prev => ({ ...prev, isFixing: true, errors: [] }));

    try {
      const missingModules = await getModulesMissingAudio();
      
      if (missingModules.length === 0) {
        setState(prev => ({ ...prev, isFixing: false }));
        return { success: true, processed: 0 };
      }

      const result = await generateMissingAudio(missingModules);
      
      setState(prev => ({ ...prev, isFixing: false }));

      // Re-analyze after fix
      await analyzeContentQuality();

      return result;
    } catch (error) {
      console.error('Audio fix failed:', error);
      setState(prev => ({
        ...prev,
        isFixing: false,
        errors: [error instanceof Error ? error.message : 'Audio fix failed']
      }));
    }
  }, [canManageContent, analyzeContentQuality]);

  return {
    ...state,
    canManageContent,
    analyzeContentQuality,
    fixCorruptedContent,
    fixMissingAudio
  };
};